import { AfterViewInit, Component, ElementRef, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Feature, MapBrowserEvent } from 'ol';
import { Cluster } from 'ol/source';
import { environment } from 'src/environments/environment';
import * as Proj from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import Point from 'ol/geom/Point';
import { RegulacionPlazaModel } from 'src/app/core/models/regulacion-plaza.model';
import * as olExtent from 'ol/extent';
import * as textStyle from 'ol/style/Text';
import IconAnchorUnits from 'ol/style/IconAnchorUnits';
import { TranslateService } from '@ngx-translate/core';
import { OlMapBaseComponent } from '../ol-map-base/ol-map-base.component';
import Layer from 'ol/layer/Layer';

const DEFAULT_ANIMATION_MILLISECONDS = 500;
const CLUSTER_PIXEL_DISTANCE = 38;

const VE_TYPE = 1;
const PMR_TYPE = 2;
const CYD_TYPE = 3;

@Component({
  selector: 'dum-ol-map-plazas',
  templateUrl: './ol-map-plazas.component.html',
  styleUrls: ['./ol-map-plazas.component.scss']
})
export class OlMapPlazasComponent extends OlMapBaseComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() width?: string | number = environment.default_width_map;
  @Input() height?: string | number = environment.default_height_map;
  @Input() plazas?: RegulacionPlazaModel[] = [];
  @Input() isAdmin?: boolean = false;
  @Input() lat: number;
  @Input() lon: number;
  @Input() changePlazas: boolean;

  srcIconOrigen: string;
  srcIconDestino: string;
  zoom: number = 15;
  plazasCluster: Cluster;
  itemLayers = [
    {
      label: `${this.translateService.instant('GLOBAL.LAYERSWITCHER.VE_TYPE')}`,
      active: true,
      type: VE_TYPE
    }, {
      label: `${this.translateService.instant('GLOBAL.LAYERSWITCHER.PMR_TYPE')}`,
      active: true,
      type: PMR_TYPE
    }, {
      label: `${this.translateService.instant('GLOBAL.LAYERSWITCHER.CYD_TYPE')}`,
      active: true,
      type: CYD_TYPE
    }
  ];

  private vectorLayer: VectorLayer;

  constructor(
    protected elementRef: ElementRef,
    protected translateService: TranslateService) {
    super(elementRef, translateService);
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.updatePlazasLayers();
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.lat = this.lat ? this.lat : environment.default_lat_map;
    this.lon = this.lon ? this.lon : environment.default_lon_map;
    this.initMap('popup-plazas', 'popup-plaza-content', this.lon, this.lat, this.zoom);
    this.createPlazasLayers();
  }

  updatePlazasLayers() {
    this.loadFeatures(false);
  }

  createPlazasLayers() {
    const features: Feature[] = [];
    const styleFunction = (feature: Feature, resolution) => this.vectorStyleFunction(feature, resolution);
    this.plazasCluster = new Cluster({
      distance: CLUSTER_PIXEL_DISTANCE,
      source: new VectorSource({
        features
      })
    });
    this.vectorLayer = new VectorLayer({
      source: this.plazasCluster,
      style: styleFunction
    });
    this.map.addLayer(this.vectorLayer);
    this.loadFeatures(true);
    this.setClickEventPlazaMarker();
  }

  protected loadFeatures(zoomToExtent: boolean) {
    if (this.vectorLayer) {
      (this.vectorLayer.getSource() as Cluster).getSource().clear();
      const features: Feature[] = [];
      this.plazas.forEach((regulacionPlaza) => {
        const item = this.itemLayers.find((itemLyr) => itemLyr.type === regulacionPlaza.plaza.tipoPlaza.id);
        if (item && item.active) {
          features.push(new Feature({
            estadoPlaza_id: regulacionPlaza.plaza.estadoPlaza.id,
            tipoPlaza_id: regulacionPlaza.plaza.tipoPlaza.id,
            geometry: new Point(Proj.fromLonLat([regulacionPlaza.plaza.localizacion.coordinates[1], regulacionPlaza.plaza.localizacion.coordinates[0]])),
            direccion: regulacionPlaza.plaza.direccion,
            ocupacion: regulacionPlaza.plaza.estadoPlaza.nombre,
            tipoAparcamiento: regulacionPlaza.plaza.tipoAparcamiento !== undefined ? regulacionPlaza.plaza.tipoAparcamiento.nombre : null,
            tipoPlaza: regulacionPlaza.plaza.tipoPlaza.nombre,
            coordinate: regulacionPlaza.plaza.localizacion.coordinates,
            tiempoMaxPermitido: regulacionPlaza.tiempoMaxPermitido,
            franjas: regulacionPlaza.franjasHorarias,
            pma: regulacionPlaza.pma,
            idRegulacionPlaza: regulacionPlaza.id,
            idPlaza: regulacionPlaza.plaza.id
          }));
        }
      });
      (this.vectorLayer.getSource() as Cluster).getSource().addFeatures(features);
      if (zoomToExtent) {
        const extent: olExtent.Extent = (this.vectorLayer.getSource() as Cluster).getSource().getExtent();
        if (extent[0] !== Number.POSITIVE_INFINITY) {
          if (!this.isEmptyExtent(extent)) {
            this.fitExtent(extent, {
              duration: DEFAULT_ANIMATION_MILLISECONDS
            }, true);
          }
        }
      }
    }
  }

  protected vectorStyleFunction(feature: Feature, resolution) {
    let style;
    const size = feature.get('features').length;
    if (size === 1) {
      style = new Style({
        image: new Icon({
          anchor: [0.5, 0.5],
          src: this.getIconPlaza(feature.get('features')[0]),
          scale: 0.5,
        })
      });
    } else {
      style = new Style({
        image: new Icon({
          anchorXUnits: IconAnchorUnits.FRACTION,
          anchorYUnits: IconAnchorUnits.FRACTION,
          anchor: [0.5, 0.5],
          src: environment.icon_map.cluster,
          scale: 1,
        }),
        text: new textStyle.default({
          text: size.toString(),
          offsetY: -2,
          stroke: new Stroke({
            color: '#fff',
            width: 1
          }),
          textBaseline: 'middle',
          font: '12px Calibri,sans-serif',
          textAlign: 'center',
          fill: new Fill({
            color: '#fff'
          })
        })
      });
    }
    return style;
  }

  protected setSize() {
    if (this.mapEl) {
      const styles = this.mapEl.style;
      styles.height = coerceCssPixelValue(this.height) || environment.default_height_map;
      styles.width = coerceCssPixelValue(this.width) || environment.default_width_map;
    }
  }

  getIconPlaza(feature: Feature) {
    switch (feature.get('tipoPlaza_id')) {
      case VE_TYPE:
        if (feature.get('estadoPlaza_id') === environment.estados_plaza.DISPONIBLE) {
          return environment.icon_map.VE.green;
        } else if (feature.get('estadoPlaza_id') === environment.estados_plaza.OCUPADA) {
          return environment.icon_map.VE.red;
        } else if (feature.get('estadoPlaza_id') === environment.estados_plaza.NO_DISPONIBLE) {
          return environment.icon_map.VE.black;
        } else if (feature.get('estadoPlaza_id') === environment.estados_plaza.ALARMA_ACTIVA) {
          return environment.icon_map.VE.blue;
        } else {
          return environment.icon_map.VE.black;
        }
      case PMR_TYPE:
        /* if (feature.get('estadoPlaza_id') === environment.estados_plaza.DISPONIBLE) {
           return environment.icon_map.PMR.green;
         } else if ( feature.get('estadoPlaza_id') === environment.estados_plaza.OCUPADA) {
           return environment.icon_map.PMR.red;
         } else if ( feature.get('estadoPlaza_id') === environment.estados_plaza.NO_DISPONIBLE) {
           return environment.icon_map.PMR.black;
         } else if ( feature.get('estadoPlaza_id') === environment.estados_plaza.ALARMA_ACTIVA) {
           return environment.icon_map.PMR.blue;
         } else {*/
        return environment.icon_map.PMR.default;
      // }
      case CYD_TYPE:
        if (feature.get('estadoPlaza_id') === environment.estados_plaza.DISPONIBLE) {
          return environment.icon_map.CyD.green;
        } else if (feature.get('estadoPlaza_id') === environment.estados_plaza.OCUPADA) {
          return environment.icon_map.CyD.red;
        } else if (feature.get('estadoPlaza_id') === environment.estados_plaza.NO_DISPONIBLE) {
          return environment.icon_map.CyD.black;
        } else if (feature.get('estadoPlaza_id') === environment.estados_plaza.ALARMA_ACTIVA) {
          return environment.icon_map.CyD.blue;
        } else {
          return environment.icon_map.CyD.black;
        }
      default:
        return environment.icon_map.CyD.black;
    }
  }

  public setClickEventPlazaMarker() {
    this.map.on('pointermove', (evt) => {
      const hit = this.map.forEachFeatureAtPixel(evt.pixel, (feature: Feature) => {
        return (feature && (feature.get('features')));
      });
      if (hit) {
        this.map.getTargetElement().style.cursor = 'pointer';
      } else {
        this.map.getTargetElement().style.cursor = '';
      }
    });
    this.map.on('singleclick', (evt) => {
      this.displayInfoPlaza(evt);
    });
  }

  private expandClusterFeature(clusterFeature: Feature, evt: MapBrowserEvent) {
    const originalFeatures = clusterFeature.get('features');
    let extent: olExtent.Extent = olExtent.createEmpty();
    originalFeatures.forEach((f: Feature) => {
      extent = olExtent.extend(extent, f.getGeometry().getExtent());
    });
    if (extent[0] !== Number.POSITIVE_INFINITY) {
      if (!this.isEmptyExtent(extent)) {
        this.fitExtent(extent, {
          duration: DEFAULT_ANIMATION_MILLISECONDS
        }, true);
      } else {
        this.showPopup(originalFeatures, evt);
      }
    }
  }

  activateItem(item) {
    item.active = !item.active;
    this.loadFeatures(false);
  }

  private showPopup(features: Feature[], evt: MapBrowserEvent) {
    const info = [];
    let existFeature = false;
    let coordinates = null;
    let first = true;
    features.forEach((feature: Feature) => {
      if (feature && feature.get('idPlaza')) {

        if (!first) {
          info.push('<span></span>');
        }

        let infoFranja = '';
        if (feature.get('tipoAparcamiento')) {
          info.push(`<b>Tipo aparcamiento :</b> ${feature.get('tipoAparcamiento')}`);
        }
        info.push(`<b>Dirección: </b> ${feature.get('direccion')}`);
        if (feature.get('franjas')) {
          feature.get('franjas').forEach((franja) => {
            infoFranja += `${franja.horaInicio}/${franja.horaFin}<br/>`;
          });
        }

        info.push(`<b>Tipo de plaza : </b>${feature.get('tipoPlaza')}`);
        if (feature.get('tipoPlaza') !== 'PMR') {
          info.push(`<b>Estado :</b> ${feature.get('ocupacion')}`);
          info.push(`<b>Tiempo max permitido :</b>${feature.get('tiempoMaxPermitido')}min`);
          info.push(`<b>PMA :</b>${feature.get('pma')}`);
          info.push(`<b>Franjas Horarias :</b>${infoFranja}`);
        }

        if (this.isAdmin) {
          info.push(`<a href="area-privada/regulacion-plaza/${feature.get('idRegulacionPlaza')}"> Ir a regulación </a>`);
          info.push(`<a href="area-privada/plaza/${feature.get('idPlaza')}"> Ir a Plaza </a>`);
        }

        existFeature = true;
        if (!coordinates) {
          coordinates = this.getHitCoordinates(this.map.getCoordinateFromPixel(evt.pixel), feature.getGeometry());
        }
        first = false;
      }
    });

    if (existFeature) {
      this.contentPopup.innerHTML = info.join('<br/>');
      setTimeout(() => {
        this.overlayPopUp.setOffset([0, -20]);
        this.overlayPopUp.setPosition(coordinates ? coordinates : evt.coordinate);
      }, 0);

    } else {
      this.overlayPopUp.setPosition(undefined);
    }
  }

  private displayInfoPlaza = (evt: MapBrowserEvent) => {
    this.map.forEachFeatureAtPixel(evt.pixel, (feature: Feature) => {
      if (this.isClusterFeature(feature)) {
        this.expandClusterFeature(feature, evt);
      } else {
        this.showPopup(feature.get('features'), evt);
      }
    });
  }

  closePopUp() {
    this.overlayPopUp.setPosition(undefined);
    return false;
  }
}

const cssUnitsPattern = /([A-Za-z%]+)$/;

function coerceCssPixelValue(value: any): string {
  if (value == null) {
    return '';
  }

  return cssUnitsPattern.test(value) ? value : `${value}px`;
}
